// presentation/hooks/shared/useFbMsnUrl.js
import { useCallback } from 'react';

/**
 * Hook compartido para obtener la URL de un mensaje de Facebook Messenger.
 * Evita renderizados innecesarios al exponer una función memoizada.
 *
 * @returns {function} getFbMsnUrl - función para obtener la URL del mensaje
 */
export default function useFbMsnUrl() {
  /**
   * Obtiene la URL de un mensaje de Facebook Messenger.
   * @param {object} message - Objeto mensaje
   * @returns {Promise<string>} - Promesa que resuelve con la URL o rechaza si falla
   */
  const getFbMsnUrl = useCallback((message) => {
    return new Promise((resolve, reject) => {
      const mid = message.mid || message.identifier;
      const fallbackUrl = message.url || message.contentMediaUrl;

      if (!mid) {
        resolve(fallbackUrl);
        return;
      }

      let targetMid = mid;
      let index = 0;
      const splitedMid = mid.split('___');
      if (splitedMid.length > 1) {
        [targetMid, index] = splitedMid;
      }
      try {
        // eslint-disable-next-line no-undef
        FB.api(
          `/${targetMid}`,
          'get',
          { access_token: ENV.FANPAGE_TOKEN, fields: 'message, attachments' },
          (response) => {
            if (response && !response.error) {
              if (
                response.attachments &&
                response.attachments.data &&
                response.attachments.data[index] &&
                response.attachments.data[index].image_data
              ) {
                const url = response.attachments.data[index].image_data.url;
                resolve(url);
              } else if (
                response.attachments &&
                response.attachments.data &&
                response.attachments.data[index] &&
                response.attachments.data[index].file_url
              ) {
                const url = response.attachments.data[index].file_url;
                resolve(url);
              } else if (
                response.attachments &&
                response.attachments.data &&
                response.attachments.data[index] &&
                response.attachments.data[index].video_data
              ) {
                const url = response.attachments.data[index].video_data.url;
                resolve(url);
              } else {
                resolve(fallbackUrl);
              }
            } else {
              resolve(fallbackUrl);
            }
          }
        );
      } catch (e) {
        resolve(fallbackUrl);
      }
    });
  }, []);

  return getFbMsnUrl;
}
